import { useEffect, useMemo, useState } from 'react';
import '../css/ApacheSuperset.css';
import api from '../services/api';

const EMPTY_DATA = { casos: 0, contactos: 0, pendientes: 0, territorios: [], tendencia: [] };
const COLORS = ['cases', 'historic', 'territory-orange'];

const SupersetTerritorialView = ({ scope, appliedFilters, catalogOptions }) => {
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const params = useMemo(() => {
    const findByName = (items, name, idField) => {
      if (!name || /^Tod[oa]s$/i.test(name)) return null;
      const item = (items || []).find(
        (option) => String(option.Nombre).toLocaleLowerCase() === String(name).toLocaleLowerCase()
      );
      return item?.[idField] || null;
    };
    const csv = (value) => Array.isArray(value) ? value.join(',') || undefined : value || undefined;
    return {
      idPatologia: scope?.idPatologia,
      idZonaSanitaria: scope?.idZonaSanitaria,
      idAreaProgramatica: scope?.idAreaProgramatica,
      anio: appliedFilters?.anio || undefined,
      semanas: csv(appliedFilters?.semana),
      provincia: appliedFilters?.provincia || undefined,
      departamentos: csv(appliedFilters?.departamento),
      municipios: csv(appliedFilters?.municipio),
      idSector: findByName(catalogOptions?.sectores, appliedFilters?.sector, 'IdSector'),
      idSubsector: findByName(catalogOptions?.subsectores, appliedFilters?.subsector, 'IdSubsector'),
      idInstitucion: findByName(catalogOptions?.instituciones, appliedFilters?.efector, 'IdInstitucionSeguimientoTratamiento'),
      idEstadoCaso: findByName(catalogOptions?.estados, appliedFilters?.estadoCaso, 'IdEstado'),
      idEstadoSeguimiento: findByName(catalogOptions?.estados, appliedFilters?.estadoSeguimiento, 'IdEstado'),
    };
  }, [scope, appliedFilters, catalogOptions]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/analytics/territorial', { params })
      .then(({ data: response }) => { if (active) setData(response); })
      .catch(() => { if (active) setError('No se pudieron cargar los indicadores territoriales.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params]);

  const territories = data.territorios || [];
  const maxLoad = Math.max(1, ...territories.map((item) => item.casos + item.contactos));
  const topTerritories = territories.slice(0, 8);
  const trendTerritories = territories.slice(0, 3).map((item) => item.territorio);
  const trendRows = data.tendencia || [];
  const weeks = [...new Set(trendRows.map((item) => item.semana))].sort((a, b) => a - b);
  const trendMax = Math.max(1, ...trendRows.map((item) => item.casos));
  const trendLines = trendTerritories.map((territory) => {
    const byWeek = new Map(trendRows.filter((row) => row.territorio === territory).map((row) => [row.semana, row.casos]));
    const points = weeks.map((week, index) => {
      const x = weeks.length === 1 ? 320 : 45 + (index / (weeks.length - 1)) * 555;
      const y = 145 - ((byWeek.get(week) || 0) / trendMax) * 100;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return { territory, points };
  });
  const maxCases = Math.max(1, ...territories.map((item) => item.casos));
  const interpretation = (item) => {
    const ratio = item.casos / maxCases;
    if (!item.casos && !item.contactos) return 'Sin registros';
    if (ratio >= 0.67) return 'Alta carga';
    if (ratio >= 0.34) return 'Carga media';
    return 'Baja carga';
  };
  const tone = (item) => {
    const ratio = (item.casos + item.contactos) / maxLoad;
    if (ratio >= 0.67) return 3;
    if (ratio >= 0.34) return 2;
    if (ratio > 0) return 1;
    return 0;
  };

  return (
    <section className="situation-view territorial-view">
      <nav className="situation-view__tabs">
        {['Resumen', 'Situación', 'Contactos', 'Territorio', 'Sector/Efector', 'Calidad', 'Patología', 'Descarga'].map((tab) => (
          <button className={tab === 'Territorio' ? 'is-active' : ''} type="button" key={tab}>{tab}</button>
        ))}
      </nav>
      <div className="situation-view__body territorial-view__body">
        <h1 className="situation-view__title territorial-view__title">Análisis territorial</h1>
        {error && <p className="situation-view__error territorial-view__error" role="alert">{error}</p>}
        <article className={`situation-card territorial-map ${loading ? 'is-loading' : ''}`}>
          <h2>Distribución territorial</h2><p>Intensidad real por área programática, municipio o localidad</p>
          <div className="territorial-map__content">
            {territories.length ? <div className="territorial-map__grid territorial-map__grid--real">
              {territories.map((item) => <i className={`tone-${tone(item)}`} key={item.territorio} title={`${item.territorio}: ${item.casos} casos, ${item.contactos} contactos`}><span>{item.territorio}</span></i>)}
            </div> : <p className="real-bars-empty">{loading ? 'Consultando VEC…' : 'Sin territorios para los filtros seleccionados'}</p>}
            <div className="territorial-map__legend">
              <span>Territorios: <b>{territories.length}</b></span><span>Casos: <b>{data.casos || 0}</b></span><span>Contactos: <b>{data.contactos || 0}</b></span><small>Pendientes: {data.pendientes || 0}</small>
            </div>
          </div>
        </article>
        <article className="situation-card territorial-comparison"><h2>Comparación de territorios</h2><p>Casos únicos registrados</p>
          {topTerritories.length ? topTerritories.map((item) => <div key={item.territorio}><span title={item.territorio}>{item.territorio}</span><i style={{ width: `${Math.max(4, (item.casos * 100) / maxCases)}%` }} /><b>{item.casos}</b></div>) : <p className="real-bars-empty">Sin datos para comparar</p>}
        </article>
        <article className="situation-card situation-card--trend territorial-trend"><h2>Tendencia por territorio seleccionado</h2><p>{trendTerritories.length ? trendTerritories.join(' · ') : 'Sin territorios con tendencia'}</p><svg viewBox="0 0 620 180" role="img" aria-label="Tendencia semanal por territorio"><line x1="45" x2="600" y1="145" y2="145"/><line x1="45" x2="600" y1="95" y2="95"/><line x1="45" x2="600" y1="45" y2="45"/>{trendLines.length ? trendLines.map((line, index) => <polyline key={line.territory} className={COLORS[index]} points={line.points}/>) : <text className="empty-chart" x="310" y="95">Sin tendencia semanal</text>}{weeks.length ? <><text x="45" y="168">SE {weeks[0]}</text><text x="555" y="168">SE {weeks[weeks.length - 1]}</text></> : null}</svg></article>
        <article className="situation-card territorial-table"><h2>Tabla territorial resumida</h2><p>Casos, contactos y pendientes por territorio</p>
          {territories.length ? <table><thead><tr><th>Territorio</th><th>Casos</th><th>Contactos</th><th>Pend.</th><th>Interpretación</th></tr></thead><tbody>{territories.slice(0, 12).map((item) => <tr key={item.territorio}><td title={item.nivel}>{item.territorio}</td><td>{item.casos}</td><td>{item.contactos}</td><td>{item.pendientes}</td><td>{interpretation(item)}</td></tr>)}</tbody></table> : <p className="real-bars-empty">Sin filas territoriales</p>}
        </article>
        <article className="situation-card situation-notes territorial-notes"><h2>Fuente y criterio territorial</h2><p>Datos reales de VEC</p><ul><li>Prioridad: área programática → municipio/localidad → departamento → provincia.</li><li>Casos y contactos se cuentan como personas únicas.</li><li>Pendiente: contacto sin fecha de cierre ni estado cerrado.</li><li>La intensidad compara la carga dentro de la selección vigente.</li></ul><small className="situation-source">Fuente: {data.fuente || 'VEC'} · Último registro: {data.ultimaFechaRegistro ? new Date(data.ultimaFechaRegistro).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : 'sin fecha'} · Consulta: {data.actualizadoEn ? new Date(data.actualizadoEn).toLocaleString('es-AR') : '—'}</small></article>
      </div>
    </section>
  );
};

export default SupersetTerritorialView;
